import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiResponse } from '@demoops/shared-types';
import { equipmentKeys } from './useEquipment';
import type { CreateMoveData, EquipmentMove, ProjectEquipmentRequest, RequestEquipmentData } from '../types';

/** GET /equipment/{id}/moves — the body is {data, meta}, not an ApiResponse
 *  envelope, so the whole body is the query result (as useEquipmentEvents does). */
export interface EquipmentMovesPage {
  data: EquipmentMove[];
  meta: { truncated: boolean; limit: number };
}

/** A machine's move history, newest first (fleet moves ledger, 2026-08-28). */
export function useEquipmentMoves(equipmentId: string | undefined) {
  return useQuery({
    queryKey: equipmentKeys.moves(equipmentId ?? ''),
    queryFn: async () => api.get<EquipmentMovesPage>(`/api/v1/equipment/${equipmentId}/moves`),
    enabled: !!equipmentId,
    retry: false,
  });
}

/**
 * Every move write lands here (fleet step 3, 2026-08-29). A move changes
 * where the machine is (its detail + the ledger), which job lists it (the
 * project Equipment tab reads useEquipmentList({project_id}) — a list),
 * what Vic's inbox shows, and every job's Requests block (a request, a send,
 * a cancel — the prefix covers whichever job it was). The machine id is
 * optional because a request may name no machine yet.
 */
export function invalidateAfterMove(qc: QueryClient, equipmentId?: string | null) {
  if (equipmentId) {
    qc.invalidateQueries({ queryKey: equipmentKeys.detail(equipmentId) });
    qc.invalidateQueries({ queryKey: equipmentKeys.moves(equipmentId) });
  }
  qc.invalidateQueries({ queryKey: equipmentKeys.lists() });
  qc.invalidateQueries({ queryKey: equipmentKeys.commandCenter() });
  // The board and timeline draw the ledger (step 4) — any move redraws them.
  qc.invalidateQueries({ queryKey: equipmentKeys.boards() });
  qc.invalidateQueries({ queryKey: equipmentKeys.timelines() });
  qc.invalidateQueries({ queryKey: equipmentKeys.inbox() });
  qc.invalidateQueries({ queryKey: [...equipmentKeys.all, 'requests'] });
}

/** GET /equipment-requests?project_id= (gate equipment.request | approve_moves):
 *  a job's open asks — `requested`, and `approved` until it leaves — oldest
 *  first, with `can_cancel` answered per viewer by the server. */
export function useProjectEquipmentRequests(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: equipmentKeys.requests(projectId ?? ''),
    queryFn: async () =>
      (await api.get<ApiResponse<ProjectEquipmentRequest[]>>(`/api/v1/equipment-requests?project_id=${encodeURIComponent(projectId ?? '')}`)).data,
    enabled: enabled && !!projectId,
    retry: false,
  });
}

/** POST /equipment-requests (gate equipment.request): a job asks for a
 *  machine. 409 = a duplicate open need for that job and category, or the
 *  machine in mind is refused — the message says which. */
export function useRequestEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: RequestEquipmentData) =>
      (await api.post<ApiResponse<EquipmentMove>>('/api/v1/equipment-requests', data)).data,
    onSuccess: (move) => invalidateAfterMove(qc, move?.equipment_id),
  });
}

/** POST /equipment/{id}/moves (gate equipment.approve_moves): an approver
 *  sends the machine. A 409 names the overlapping claim; re-post the same
 *  body with `override_reason` to override it. */
export function useCreateMove(equipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateMoveData) =>
      (await api.post<ApiResponse<EquipmentMove>>(`/api/v1/equipment/${equipmentId}/moves`, data)).data,
    onSuccess: () => invalidateAfterMove(qc, equipmentId),
  });
}

/** POST /equipment-moves/{id}/cancel — a requester may cancel their own
 *  `requested` move without a reason; an approver must give one. */
export function useCancelMove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ moveId, reason }: { moveId: string; reason?: string }) =>
      (await api.post<ApiResponse<EquipmentMove>>(`/api/v1/equipment-moves/${moveId}/cancel`, reason ? { reason } : {})).data,
    onSuccess: (move) => invalidateAfterMove(qc, move?.equipment_id),
  });
}

/** POST /equipment-moves/{id}/depart — it left; on the road until arrive. */
export function useDepartMove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ moveId, haul_notes, meter }: { moveId: string; haul_notes?: string; meter?: number }) =>
      (await api.post<ApiResponse<EquipmentMove>>(`/api/v1/equipment-moves/${moveId}/depart`, { haul_notes, meter })).data,
    onSuccess: (move) => invalidateAfterMove(qc, move?.equipment_id),
  });
}

/** POST /equipment-moves/{id}/arrive — it is there; the ledger takes the meter. */
export function useArriveMove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ moveId, meter, haul_notes }: { moveId: string; meter?: number; haul_notes?: string }) =>
      (await api.post<ApiResponse<EquipmentMove>>(`/api/v1/equipment-moves/${moveId}/arrive`, { meter, haul_notes })).data,
    onSuccess: (move) => invalidateAfterMove(qc, move?.equipment_id),
  });
}
